import type { EntityType } from '@zag/auth/models';

export type PermissionScope = 'scoped' | 'full';
export type PermissionScopeState = 'inactive' | PermissionScope;
export type PermissionScopeSelection = Map<string, PermissionScope>;

export interface PermissionScopeMetadata {
  id: string;
  scopeEntityType?: EntityType | null;
  requiresPermissionId?: string | null;
}

export interface PermissionScopeChange {
  id: string;
  scope: PermissionScopeState;
}

const SCOPE_RANK: Record<PermissionScopeState, number> = { inactive: 0, scoped: 1, full: 2 };

export function scopeRank(state: PermissionScopeState): number {
  return SCOPE_RANK[state];
}

export function isScopeablePermission(item: PermissionScopeMetadata | undefined): boolean {
  return (item?.scopeEntityType ?? null) !== null;
}

function rankOf(selection: ReadonlyMap<string, PermissionScope>, id: string): number {
  return SCOPE_RANK[selection.get(id) ?? 'inactive'];
}

function setRanked(
  result: PermissionScopeSelection,
  catalog: ReadonlyMap<string, PermissionScopeMetadata>,
  id: string,
  rank: number
): void {
  if (rank <= 0) {
    result.delete(id);
    return;
  }
  // Non-scopeable rows only support inactive/full, so any positive rank lands on full.
  const scope: PermissionScope = rank >= SCOPE_RANK['full'] || !isScopeablePermission(catalog.get(id)) ? 'full' : 'scoped';
  result.set(id, scope);
}

function raisePrerequisites(result: PermissionScopeSelection, byId: ReadonlyMap<string, PermissionScopeMetadata>, id: string): void {
  const needed = rankOf(result, id);
  const seen = new Set<string>();
  let cursor = byId.get(id)?.requiresPermissionId ?? null;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    if (rankOf(result, cursor) < needed) setRanked(result, byId, cursor, needed);
    cursor = byId.get(cursor)?.requiresPermissionId ?? null;
  }
}

function minPrereqRank(result: PermissionScopeSelection, byId: ReadonlyMap<string, PermissionScopeMetadata>, id: string): number {
  let min = SCOPE_RANK['full'];
  let found = false;
  const seen = new Set<string>();
  let cursor = byId.get(id)?.requiresPermissionId ?? null;
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    found = true;
    const rank = rankOf(result, cursor);
    if (rank < min) min = rank;
    cursor = byId.get(cursor)?.requiresPermissionId ?? null;
  }
  return found ? min : SCOPE_RANK['full'];
}

function cascadeToDependents(
  result: PermissionScopeSelection,
  byId: ReadonlyMap<string, PermissionScopeMetadata>,
  children: ReadonlyMap<string, string[]>,
  originId: string
): void {
  const queue = [...(children.get(originId) ?? [])];
  const seen = new Set(queue);
  while (queue.length > 0) {
    const depId = queue.shift() as string;
    const allowed = minPrereqRank(result, byId, depId);
    if (rankOf(result, depId) > allowed) {
      // Non-scopeable rows cannot hold scoped, so a scoped cap removes them instead.
      if (!isScopeablePermission(byId.get(depId)) && allowed === SCOPE_RANK['scoped']) result.delete(depId);
      else setRanked(result, byId, depId, allowed);
    }
    for (const next of children.get(depId) ?? []) {
      if (!seen.has(next)) {
        seen.add(next);
        queue.push(next);
      }
    }
  }
}

function buildChildren(catalog: readonly PermissionScopeMetadata[]): Map<string, string[]> {
  const children = new Map<string, string[]>();
  for (const item of catalog) {
    if (!item.requiresPermissionId) continue;
    const list = children.get(item.requiresPermissionId);
    if (list) list.push(item.id);
    else children.set(item.requiresPermissionId, [item.id]);
  }
  return children;
}

function chainDepth(byId: ReadonlyMap<string, PermissionScopeMetadata>, id: string, within: ReadonlySet<string>): number {
  let depth = 0;
  const seen = new Set<string>();
  let cursor = byId.get(id)?.requiresPermissionId ?? null;
  while (cursor && within.has(cursor) && !seen.has(cursor)) {
    seen.add(cursor);
    depth++;
    cursor = byId.get(cursor)?.requiresPermissionId ?? null;
  }
  return depth;
}

export function applyScopeChange(
  selection: ReadonlyMap<string, PermissionScope>,
  catalog: readonly PermissionScopeMetadata[],
  id: string,
  next: PermissionScopeState
): PermissionScopeSelection {
  const byId = new Map(catalog.map(item => [item.id, item]));
  const result: PermissionScopeSelection = new Map(selection);
  if (!isScopeablePermission(byId.get(id)) && next === 'scoped') result.set(id, 'full');
  else if (next === 'inactive') result.delete(id);
  else result.set(id, next);
  raisePrerequisites(result, byId, id);
  cascadeToDependents(result, byId, buildChildren(catalog), id);
  return result;
}

export function applyGroupScopeChange(
  selection: ReadonlyMap<string, PermissionScope>,
  catalog: readonly PermissionScopeMetadata[],
  memberIds: readonly string[],
  target: PermissionScopeState
): PermissionScopeSelection {
  const byId = new Map(catalog.map(item => [item.id, item]));
  const members = new Set(memberIds);
  // Clearing starts at prerequisites so cascades settle; enabling starts at dependents so raise-chains settle.
  const ordered = [...memberIds].sort((a, b) =>
    target === 'inactive'
      ? chainDepth(byId, a, members) - chainDepth(byId, b, members)
      : chainDepth(byId, b, members) - chainDepth(byId, a, members)
  );
  let result: PermissionScopeSelection = new Map(selection);
  for (const id of ordered) result = applyScopeChange(result, catalog, id, target);
  return result;
}
